import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Slider from 'react-slick';
import isEmpty from 'lodash/isEmpty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faYoutube as fabFaYoutube } from '@fortawesome/free-brands-svg-icons';
import { FormattedMessage } from 'react-intl';
import { Modal, ModalBody, Button } from 'reactstrap';

import FluidImg from 'Components/Utility/FluidImg';
import AutoPlayVideo from 'Components/Utility/Video/AutoPlayVideo';
import CarouselPaging from 'Components/Carousel/CarouselPaging';
import CarouselArrow from 'Components/Carousel/CarouselArrow';

const PublicationSlickCarousel = ({
  comicCover,
  screenshots,
  youTubeVideos,
  carouselType,
  resetScreenshots,
  setResetScreenshots,
}) => {
  const carouselRef = useRef();
  const [modal, setModal] = useState(false);
  const [slideNumber, setSlideNumber] = useState(0);
  /**
   * Set screenshot slider index to 0. when publication in parent changes
  */
  useEffect(() => {
    if (resetScreenshots) {
      if (carouselRef?.current) carouselRef.current.slickGoTo(0);
      setResetScreenshots(false);
    }
  }, [resetScreenshots, setResetScreenshots]);

  const handleToggle = () => {
    setModal(prevState => !prevState);
  };

  const handleInterativeToggle = (sliderNumber) => {
    setModal(prevState => !prevState);
    setSlideNumber(sliderNumber);
  };

  const settings = {
    adaptiveHeight: true,
    initialSlide: slideNumber,
    centerMode: false,
    accessibility: true,
    centerPadding: '0px',
    infinite: false,
    slidesToShow: 1,
    dots: true,
    arrows: true,
    draggable: true,
    autoplay: false,
    prevArrow: <CarouselArrow ariaLabel="Previous" arrowDirection="left" containerClassName="arrow-container arrow-container-left" />,
    nextArrow: <CarouselArrow ariaLabel="Next" arrowDirection="right" containerClassName="arrow-container arrow-container-right" />,
    autoplaySpeed: 7500,
    speed: 500,
    variableWidth: false,
    dotsClass: 'slick-dots slick-thumb',
    customPaging: i => (
      <CarouselPaging slideIndex={i} slideLength={screenshots?.length} slidesToShow={1} />
    ),
    beforeChange: (i, n) => {
      if (carouselRef?.current) carouselRef.current.slickGoTo(n);
    },
    responsive: [
      {
        breakpoint: 767,
        settings: {
          dots: true,
          arrows: true,
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          dots: true,
          arrows: true,
          centerMode: true,
          centerPadding: '15px',
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (isEmpty(screenshots)) {
    return (
      <React.Fragment>
        <FluidImg
          className="img-force-full cover-background"
          src={comicCover}
          alt={comicCover}
        />
        <React.Fragment>
          <FluidImg
            className="img-force-full"
            src={comicCover}
            alt={comicCover}
          />
          {!isEmpty(youTubeVideos) &&
            <button className="btn-reset-style video-icon-container" onClick={() => { handleInterativeToggle(0); }}>
              <FontAwesomeIcon icon={fabFaYoutube} title="YouTube" />
            </button>
          }
        </React.Fragment>

        <Modal isOpen={modal} toggle={handleToggle} className={classNames('comic-preview-modal', { 'non-game-landscape': carouselType === 'non game landscape' })}>
          <Button
            color="link"
            className="close-button"
            onClick={handleToggle}
          >
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </Button>
          <ModalBody>
            {!isEmpty(youTubeVideos) &&
              <div className="video-wrapper">
                <AutoPlayVideo width="100%" height="auto" src={youTubeVideos[0]} className="youtube-video" />
              </div>
            }
          </ModalBody>
        </Modal>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Slider ref={carouselRef} {...settings} className="comic-slick-carousel">
        <div className="look-inside-img-container">
          <FluidImg className="img-force-full cover-foreground" src={comicCover} alt={comicCover} />
          <FluidImg className="img-force-full cover-background" src={comicCover} alt={comicCover} />
          <button className="btn-reset-style look-inside-button" onClick={() => handleInterativeToggle(0)} />
          <div className="look-inside">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <FormattedMessage id="publication-slick-carousel.button.look-inside" defaultMessage="Look Inside" />
          </div>
        </div>
        {screenshots && screenshots.map(({ slug, alt }, i) =>
          <div className="look-inside-img-container" key={slug}>
            <FluidImg className="img-force-full cover-foreground" src={slug} alt={alt} />
            <FluidImg className="img-force-full cover-background" src={slug} alt={alt} />
            <button className="btn-reset-style look-inside-button" onClick={() => handleInterativeToggle(i + 1)} />
            <div className="look-inside">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <FormattedMessage id="publication-slick-carousel.button.look-inside" defaultMessage="Look Inside" />
            </div>
          </div>,
        )}
      </Slider>

      <Modal isOpen={modal} toggle={handleToggle} className={classNames('comic-preview-modal', { 'non-game-landscape': carouselType === 'non game landscape' })}>
        <Button
          color="link"
          className="close-button"
          onClick={handleToggle}
        >
          <FontAwesomeIcon icon={faXmark} title="Close modal" />
        </Button>
        <ModalBody>
          <div className="preview-img-container">
            <Slider ref={carouselRef} {...settings} className="comic-slick-carousel" >
              <div className="look-inside-img-container">
                <FluidImg className="img-force-full" src={comicCover} alt={comicCover} />
              </div>
              {screenshots && screenshots.map(({ slug, alt }) =>
                <div className="look-inside-img-container" key={slug}>
                  <FluidImg className="img-force-full" src={slug} alt={alt} />
                </div>,
              )}
            </Slider>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

PublicationSlickCarousel.propTypes = {
  screenshots: PropTypes.arrayOf(PropTypes.object),
  comicCover: PropTypes.string,
  carouselType: PropTypes.string,
  youTubeVideos: PropTypes.arrayOf(PropTypes.string),
};

PublicationSlickCarousel.defaultProps = {
  screenshots: [],
  comicCover: null,
  bundleType: '',
  youTubeVideos: [],
  carouselType: '',
};

export default PublicationSlickCarousel;
